/** The state behind a table's power features, in one place - ADR-0212's table research, built once.
 *
 * A page calls `useGridPower` once, passes the result to its `FilterLine` and its `GridTable`, and
 * both draw from it: the filter line shows the bulk bar, the filter chips, the view tabs, the column
 * chooser, the density switch and Export; the grid shows the tick-boxes, the column menus, the groups
 * and the quick-peek. Neither is told anything by hand, so the line and the table cannot disagree
 * about what is selected, hidden, sorted or grouped.
 *
 * **It owns state and computes no figure.** Rows, totals and counts are the page's and the server's;
 * `sortRows` orders rows with the page's own accessors; a subtotal is the grid's, over rows it holds,
 * and says so. Nothing here fetches, and nothing here knows what a row is: the grid *binds* a small
 * description of what it shows (`bind`) so Export can write exactly that.
 *
 * **Per user, in this browser.** Density and the saved views are stored under the signed-in person's
 * id (`storage.ts`). An account-level store needs an endpoint that does not exist.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import type { FilterDef, FilterState } from "../filters";
import type { SortState } from "../sortRows";
import { sortRowsBy } from "../sortRows";
import { downloadCsv, toCsv } from "./csv";
import { DENSITIES } from "./gridTypes";
import type { GridDensity } from "./gridTypes";
import { readJson, tableKey, writeJson } from "./storage";
import { ALL_VIEW, EMPTY_CONFIG, canonical, newViewId, readViews, sameConfig, writeViews } from "./views";
import type { SavedView, ViewConfig } from "./views";

export interface BulkSelection {
  /** The ticked rows' keys, in no particular order. */
  keys: readonly string[];
  count: number;
  /** "Select all N matching" was chosen: every row the filter matches, loaded or not. */
  allMatching: boolean;
}

/** What the grid tells the power state about itself, so Export can write what is shown. */
export interface ColumnMeta {
  id: string;
  header: string;
  /** May be hidden from the chooser. */
  hideable: boolean;
  groupable: boolean;
}

export interface GridBinding {
  /** Every column the grid has (hidden ones too), for the chooser. */
  columns: readonly ColumnMeta[];
  /** The header and the cells, as text, for the visible columns (selected rows only, or every row shown). */
  matrix: (selectedOnly: boolean) => { header: string[]; rows: string[][] };
  rowCount: number;
  columnCount: number;
  /** Rows in the filter that are not in the grid yet (a paged list). */
  unlisted: number;
}

export interface GridPowerOptions {
  /** The place's stable name: `scope1`, `documents`. The views are stored under it. */
  place: string;
  /** The signed-in person's id. Until it is known, preferences are the "anon" ones. */
  userKey?: string;
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  filterDefs?: readonly FilterDef[];
  /** What the rows are, for the count: "figures". */
  resultNoun?: string;
  /** Shipped views, locked. "All" is always first and is not listed here. */
  starterViews?: readonly SavedView[];
  /** The actions the **page** offers for ticked rows. The grid adds none beyond Export selected. */
  bulkActions?: (selection: BulkSelection) => ReactNode;
  /** The export's file name, without the extension. Default: the place. */
  exportName?: string;
  defaultDensity?: GridDensity;
}

export interface ViewsApi {
  /** "All", the starters, then the person's own. */
  list: readonly SavedView[];
  activeId: string;
  /** The table differs from the active view. */
  dirty: boolean;
  defaultId: string | null;
  canSave: boolean;
  select: (id: string) => void;
  save: () => void;
  saveAsNew: (name: string) => void;
  rename: (id: string, name: string) => void;
  duplicate: (id: string) => void;
  remove: (id: string) => void;
  setDefault: (id: string | null) => void;
}

export interface GridPower {
  place: string;
  // ---- selection
  selected: ReadonlySet<string>;
  selectedCount: number;
  allMatching: boolean;
  setSelected: (next: ReadonlySet<string>) => void;
  selectAllMatching: (count: number) => void;
  clearSelection: () => void;
  bulkActions: ((selection: BulkSelection) => ReactNode) | undefined;
  // ---- filters
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  filterDefs: readonly FilterDef[];
  resultNoun: string | undefined;
  /** A column menu asked for this filter's list to open. `n` changes on every ask, so the same filter
   * can be asked for twice. */
  filterRequest: { id: string; n: number } | null;
  requestFilter: (id: string) => void;
  // ---- sort
  sorts: readonly SortState[];
  setSorts: (next: readonly SortState[]) => void;
  /** Click on a header: replaces every level. Shift-click: adds or cycles a level. */
  cycleSort: (columnId: string, additive: boolean) => void;
  sortRows: <T>(
    rows: readonly T[],
    accessors: Readonly<Record<string, ((row: T) => string | number | null | undefined) | undefined>>,
  ) => T[];
  // ---- columns
  hidden: ReadonlySet<string>;
  toggleHidden: (id: string) => void;
  showAllColumns: () => void;
  pinned: readonly string[];
  togglePin: (id: string) => void;
  // ---- grouping
  groupBy: string | null;
  setGroupBy: (id: string | null) => void;
  // ---- density
  density: GridDensity;
  setDensity: (next: GridDensity) => void;
  // ---- quick-peek
  peekKey: string | null;
  setPeekKey: (key: string | null) => void;
  // ---- saved views
  views: ViewsApi;
  // ---- shortcut sheet
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
  // ---- export
  /** Every column of the grid, hidden or not: the chooser's list. Empty until the grid has drawn. */
  columnsMeta: readonly ColumnMeta[];
  bind: (binding: GridBinding | null) => void;
  exportCsv: (selectedOnly: boolean) => void;
  exportSummary: (selectedOnly: boolean) => string;
  /** The active filters in words: "Scope: 2, Site: Depot". Empty for none. */
  describeFilters: () => string;
}

const ALL: SavedView = { id: ALL_VIEW, name: "All", locked: true, config: EMPTY_CONFIG };

function isDensity(value: unknown): value is GridDensity {
  return typeof value === "string" && (DENSITIES as readonly string[]).includes(value);
}

export function useGridPower({
  place,
  userKey,
  filters,
  onFiltersChange,
  filterDefs = [],
  resultNoun,
  starterViews = [],
  bulkActions,
  exportName,
  defaultDensity = "comfortable",
}: GridPowerOptions): GridPower {
  // ---- who the preferences belong to: re-read when the person becomes known
  const [readFor, setReadFor] = useState<string | undefined | null>(null);
  const [density, setDensityState] = useState<GridDensity>(defaultDensity);
  const [store, setStore] = useState<{ views: SavedView[]; defaultId: string | null }>({
    views: [],
    defaultId: null,
  });
  const [activeId, setActiveId] = useState<string>(ALL_VIEW);
  const [sorts, setSorts] = useState<readonly SortState[]>([]);
  const [hiddenList, setHiddenList] = useState<readonly string[]>([]);
  const [groupBy, setGroupBy] = useState<string | null>(null);
  const [pinned, setPinned] = useState<readonly string[]>([]);
  const [defaultFilters, setDefaultFilters] = useState<FilterState | null>(null);

  const starters = useMemo(() => starterViews.map((v) => ({ ...v, locked: true })), [starterViews]);
  const everyView = useMemo<readonly SavedView[]>(
    () => [ALL, ...starters, ...store.views],
    [starters, store.views],
  );

  if (readFor !== userKey) {
    // The person is known (or changed): read their density and views. State set during render, from
    // storage, once per person - the same derived-state pattern `FilterBar` uses for its search.
    setReadFor(userKey);
    const stored = readJson<unknown>(tableKey("density", userKey), null);
    if (isDensity(stored)) setDensityState(stored);
    const views = readViews(userKey, place);
    setStore(views);
    const wanted = views.defaultId;
    const view = wanted ? [ALL, ...starters, ...views.views].find((v) => v.id === wanted) : undefined;
    if (view && wanted && filters.search === "" && Object.keys(filters.values).length === 0) {
      setActiveId(view.id);
      setSorts(view.config.sorts);
      setHiddenList(view.config.hidden);
      setGroupBy(view.config.groupBy);
      setPinned(view.config.pinned);
      setDefaultFilters(view.config.filters);
    }
  }

  // A default view's filters go out through the page's own setter (the URL), which is not ours to call
  // while rendering - so they wait here for an effect, once per person.
  const latestChange = useRef(onFiltersChange);
  useEffect(() => {
    latestChange.current = onFiltersChange;
  });
  useEffect(() => {
    if (defaultFilters) latestChange.current(defaultFilters);
  }, [defaultFilters]);

  const setDensity = useCallback(
    (next: GridDensity) => {
      setDensityState(next);
      writeJson(tableKey("density", userKey), next);
    },
    [userKey],
  );

  // ---- selection
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const [all, setAll] = useState<{ count: number; key: string } | null>(null);
  const filtersKey = canonical({ ...EMPTY_CONFIG, filters });
  const allMatching = all !== null && all.key === filtersKey;
  const setSelected = useCallback((next: ReadonlySet<string>) => {
    setPicked(next);
    setAll(null);
  }, []);
  const selectAllMatching = useCallback(
    (count: number) => setAll({ count, key: filtersKey }),
    [filtersKey],
  );
  const clearSelection = useCallback(() => {
    setPicked(new Set());
    setAll(null);
  }, []);
  const selectedCount = allMatching && all ? Math.max(all.count, picked.size) : picked.size;

  // ---- filters
  const [filterRequest, setFilterRequest] = useState<{ id: string; n: number } | null>(null);
  const requestFilter = useCallback(
    (id: string) => setFilterRequest((prev) => ({ id, n: (prev?.n ?? 0) + 1 })),
    [],
  );

  // ---- sort
  const cycleSort = useCallback((columnId: string, additive: boolean) => {
    setSorts((prev) => {
      const at = prev.findIndex((s) => s.columnId === columnId);
      const current = at < 0 ? undefined : prev[at];
      const next: SortState | null =
        current === undefined
          ? { columnId, direction: "asc" }
          : current.direction === "asc"
            ? { columnId, direction: "desc" }
            : null;
      if (!additive) return next ? [next] : [];
      if (at < 0) return next ? [...prev, next] : prev;
      return next ? prev.map((s, i) => (i === at ? next : s)) : prev.filter((_, i) => i !== at);
    });
  }, []);
  const sortRows = useCallback(
    <T,>(
      rows: readonly T[],
      accessors: Readonly<Record<string, ((row: T) => string | number | null | undefined) | undefined>>,
    ) => sortRowsBy(rows, sorts, accessors),
    [sorts],
  );

  // ---- columns
  const hidden = useMemo<ReadonlySet<string>>(() => new Set(hiddenList), [hiddenList]);
  const toggleHidden = useCallback(
    (id: string) =>
      setHiddenList((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [],
  );
  const showAllColumns = useCallback(() => setHiddenList([]), []);
  const togglePin = useCallback(
    (id: string) => setPinned((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [],
  );

  // ---- saved views
  const current: ViewConfig = useMemo(
    () => ({ filters, sorts, hidden: hiddenList, groupBy, pinned }),
    [filters, sorts, hiddenList, groupBy, pinned],
  );
  const active = everyView.find((v) => v.id === activeId) ?? ALL;
  const dirty = !sameConfig(current, active.config);

  const commit = useCallback(
    (next: { views: SavedView[]; defaultId: string | null }) => {
      setStore(next);
      writeViews(userKey, place, next);
    },
    [userKey, place],
  );
  const applyConfig = useCallback(
    (config: ViewConfig) => {
      onFiltersChange(config.filters);
      setSorts(config.sorts);
      setHiddenList(config.hidden);
      setGroupBy(config.groupBy);
      setPinned(config.pinned);
      setPicked(new Set());
      setAll(null);
    },
    [onFiltersChange],
  );
  const views: ViewsApi = {
    list: everyView,
    activeId: active.id,
    dirty,
    defaultId: store.defaultId,
    canSave: dirty && !active.locked,
    select: (id) => {
      const view = everyView.find((v) => v.id === id);
      if (!view) return;
      setActiveId(view.id);
      applyConfig(view.config);
    },
    save: () => {
      if (active.locked) return;
      commit({
        ...store,
        views: store.views.map((v) => (v.id === active.id ? { ...v, config: current } : v)),
      });
    },
    saveAsNew: (name) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const view: SavedView = { id: newViewId(), name: trimmed, config: current };
      commit({ ...store, views: [...store.views, view] });
      setActiveId(view.id);
    },
    rename: (id, name) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      commit({ ...store, views: store.views.map((v) => (v.id === id && !v.locked ? { ...v, name: trimmed } : v)) });
    },
    duplicate: (id) => {
      const source = everyView.find((v) => v.id === id);
      if (!source) return;
      const view: SavedView = { id: newViewId(), name: `${source.name} copy`, config: source.config };
      commit({ ...store, views: [...store.views, view] });
      setActiveId(view.id);
      applyConfig(view.config);
    },
    remove: (id) => {
      const target = store.views.find((v) => v.id === id);
      if (!target) return;
      commit({
        views: store.views.filter((v) => v.id !== id),
        defaultId: store.defaultId === id ? null : store.defaultId,
      });
      if (activeId === id) {
        setActiveId(ALL_VIEW);
        applyConfig(EMPTY_CONFIG);
      }
    },
    setDefault: (id) => commit({ ...store, defaultId: id === ALL_VIEW ? null : id }),
  };

  // ---- export
  const binding = useRef<GridBinding | null>(null);
  const [columnsMeta, setColumnsMeta] = useState<readonly ColumnMeta[]>([]);
  const metaKey = useRef("");
  const bind = useCallback((next: GridBinding | null) => {
    binding.current = next;
    const key = next ? JSON.stringify(next.columns) : "";
    if (key !== metaKey.current) {
      metaKey.current = key;
      setColumnsMeta(next ? next.columns : []);
    }
  }, []);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const describeFilters = useCallback(() => {
    const parts: string[] = [];
    for (const def of filterDefs) {
      const values = filters.values[def.id] ?? [];
      if (values.length === 0) continue;
      const labels = values.map((value) => def.options.find((o) => o.value === value)?.label ?? value);
      parts.push(`${def.label}: ${labels.join(", ")}`);
    }
    if (filters.search.trim()) parts.push(`search "${filters.search.trim()}"`);
    return parts.join("; ");
  }, [filterDefs, filters]);
  const exportSummary = useCallback(
    (selectedOnly: boolean) => {
      const b = binding.current;
      if (!b) return "";
      const { rows } = b.matrix(selectedOnly);
      const filtered = describeFilters();
      const head = `${String(rows.length)} ${rows.length === 1 ? "row" : "rows"}, ${String(b.columnCount)} ${b.columnCount === 1 ? "column" : "columns"}`;
      const partial = b.unlisted > 0 && !selectedOnly ? `, ${String(b.unlisted)} not loaded` : "";
      const ticked = selectedOnly ? ", ticked rows" : "";
      return `${head}${partial}${ticked}${filtered ? `, filtered by ${filtered}` : ""}`;
    },
    [describeFilters],
  );
  const exportCsv = useCallback(
    (selectedOnly: boolean) => {
      const b = binding.current;
      if (!b) return;
      const { header, rows } = b.matrix(selectedOnly);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`${exportName ?? place}-${stamp}.csv`, toCsv(header, rows));
    },
    [exportName, place],
  );

  const [peekKey, setPeekKey] = useState<string | null>(null);

  return {
    place,
    selected: picked,
    selectedCount,
    allMatching,
    setSelected,
    selectAllMatching,
    clearSelection,
    bulkActions,
    filters,
    setFilters: onFiltersChange,
    filterDefs,
    resultNoun,
    filterRequest,
    requestFilter,
    sorts,
    setSorts,
    cycleSort,
    sortRows,
    hidden,
    toggleHidden,
    showAllColumns,
    pinned,
    togglePin,
    groupBy,
    setGroupBy,
    density,
    setDensity,
    peekKey,
    setPeekKey,
    views,
    shortcutsOpen,
    setShortcutsOpen,
    columnsMeta,
    bind,
    exportCsv,
    exportSummary,
    describeFilters,
  };
}
